import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Phone } from "lucide-react";
import { CLINIC, waLink, telLink } from "../../lib/clinic";
import { LANDING_PAGES } from "../../data/landingPages";

/** Floating action buttons for WhatsApp + Call.
 *  On the 8 keyword-rich service landing pages, the WhatsApp button:
 *   • pre-fills the WhatsApp message with the current service's `ctaMessage`
 *   • auto-expands the "Chat about {service}" label for 5 s on mount so mobile
 *     users (no hover) still see the CTA
 *   • re-expands on hover / focus on desktop
 *  Everywhere else, the button stays a compact icon with the generic enquiry.
 */
const FloatingActions = () => {
  const { pathname } = useLocation();
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const [autoExpanded, setAutoExpanded] = useState(false);

  const landingPage = useMemo(() => {
    const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
    return LANDING_PAGES.find((p) => p.slug === slug) || null;
  }, [pathname]);

  // Auto-open the label for 5 s whenever a landing page mounts / changes.
  useEffect(() => {
    if (!landingPage) return;
    setAutoExpanded(true);
    const t = setTimeout(() => setAutoExpanded(false), 5000);
    return () => clearTimeout(t);
  }, [landingPage]);

  const waMessage = landingPage
    ? landingPage.ctaMessage
    : "Hello Dr. Shantanu, I'd like to enquire about an appointment.";

  const serviceLabel = landingPage
    ? landingPage.keyword.replace(/\s*Varanasi\s*$/i, "").trim()
    : null;

  const expanded = hoverExpanded || autoExpanded;

  return (
    <div className="fixed bottom-6 right-4 sm:right-6 z-40 flex flex-col items-end gap-3" data-testid="floating-actions">
      <a
        href={waLink(waMessage)}
        target="_blank"
        rel="nofollow noopener noreferrer"
        aria-label={serviceLabel ? `Chat on WhatsApp about ${serviceLabel}` : "Chat on WhatsApp"}
        data-testid="float-whatsapp"
        data-context={landingPage ? "landing" : "generic"}
        onMouseEnter={() => setHoverExpanded(true)}
        onMouseLeave={() => setHoverExpanded(false)}
        onFocus={() => setHoverExpanded(true)}
        onBlur={() => setHoverExpanded(false)}
        className="group flex items-center rounded-full bg-[#25D366] text-white shadow-2xl shadow-black/20 hover:scale-[1.03] transition-all duration-300 overflow-hidden"
        style={{ height: 56 }}
      >
        <span
          className="shrink-0 flex items-center justify-center"
          style={{ width: 56, height: 56 }}
        >
          <MessageCircle size={22} strokeWidth={1.75} />
        </span>
        {landingPage && (
          <span
            className={`text-sm font-medium whitespace-nowrap transition-[max-width,opacity,padding-right] duration-300 ${
              expanded ? "max-w-[300px] opacity-100 pr-5" : "max-w-0 opacity-0 pr-0"
            }`}
            style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
            data-testid="float-whatsapp-service-label"
          >
            Chat about {serviceLabel}
          </span>
        )}
      </a>
      <a
        href={telLink()}
        aria-label="Call clinic"
        data-testid="float-call"
        className="rounded-full bg-[hsl(var(--primary))] text-white shadow-2xl shadow-black/20 flex items-center justify-center hover:scale-105 transition-transform"
        style={{ width: 56, height: 56 }}
      >
        <Phone size={20} strokeWidth={1.75} />
      </a>
    </div>
  );
};

export default FloatingActions;
